import {PropertyDefinitionForm} from '@github-ui/custom-properties-core/components/PropertyDefinitionForm'
import type {usePropertyDefinitionForm} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import type {useRefs} from '@github-ui/custom-properties-core/hooks/use-refs'
import {getEmptyValueByType} from '@github-ui/custom-properties-types/helpers'
import {Checkbox, FormControl} from '@primer/react'
import {useMemo} from 'react'

import type {RepoPropertyDefinitionWithValueSchema} from '../hooks/use-definition-form-with-value'
import styles from './MultiStepForm.module.css'

type RefProps = ReturnType<typeof useRefs<RepoPropertyDefinitionWithValueSchema>>
type FormProps = ReturnType<typeof usePropertyDefinitionForm<RepoPropertyDefinitionWithValueSchema>>

type Props = FormProps & RefProps

export function DefinitionStep({updateField, getFieldValue, getFieldError, validateField, getRef, setRef}: Props) {
  const valueType = getFieldValue('valueType')
  const isSelect = valueType === 'single_select' || valueType === 'multi_select'
  const isText = valueType === 'string'

  const setNewAllowedValueRef = useMemo(() => setRef('newAllowedValue'), [setRef])

  return (
    <PropertyDefinitionForm>
      <PropertyDefinitionForm.NameField
        className={styles.fullWidthField}
        inputRef={setRef('propertyName')}
        readonly={false}
        value={getFieldValue('propertyName')}
        validationError={getFieldError('propertyName')}
        onChange={value => updateField('propertyName', value)}
      />

      <PropertyDefinitionForm.DescriptionField
        className={styles.fullWidthField}
        inputRef={setRef('description')}
        value={getFieldValue('description') || ''}
        validationError={getFieldError('description')}
        onChange={value => updateField('description', value)}
      />

      <PropertyDefinitionForm.TypeSelector
        value={valueType}
        readonly={false}
        onChange={value => {
          updateField('valueType', value)
          updateField('appliedValue', getEmptyValueByType(value))
        }}
      />

      {isSelect && (
        <PropertyDefinitionForm.AllowedValuesEditor
          setNewAllowedValueRef={setNewAllowedValueRef}
          allowedValues={getFieldValue('allowedValues')}
          newAllowedValue={getFieldValue('newAllowedValue')}
          newAllowedValueError={getFieldError('newAllowedValue')}
          defaultValue={getFieldValue('defaultValue')}
          onNewAllowedValueChange={value => updateField('newAllowedValue', value)}
          onAllowedValuesChange={async ({type, values}) => {
            if (type === 'add') {
              if (await validateField('newAllowedValue')) {
                return getRef('newAllowedValue')?.focus()
              }
              updateField('newAllowedValue', '', {skipValidation: true})
            }

            updateField('allowedValues', values)
          }}
        />
      )}

      <PropertyDefinitionForm.AdditionalSettingsSection>
        {isText && (
          <PropertyDefinitionForm.RegexEditor
            inputRef={setRef('regexPattern')}
            isEnabled={getFieldValue('regexEnabled')}
            pattern={getFieldValue('regexPattern') || ''}
            validationError={getFieldError('regexPattern')}
            onEnabledChange={enabled => updateField('regexEnabled', enabled)}
            onPatternChange={pattern => updateField('regexPattern', pattern, {debounce: true})}
          />
        )}
        <FormControl>
          <Checkbox
            checked={getFieldValue('valuesEditableBy') === 'org_and_repo_actors'}
            onChange={({target}) => {
              updateField('valuesEditableBy', target.checked ? 'org_and_repo_actors' : 'org_actors')
              if (!target.checked) {
                updateField('requireExplicitValues', false)
              }
            }}
          />
          <FormControl.Label>Allow repository actors to set this property</FormControl.Label>
          <FormControl.Caption>
            Repository users and apps with the repository-level &quot;custom properties&quot; fine-grained permission
            can set and update the value for their repository.
          </FormControl.Caption>
        </FormControl>
        <PropertyDefinitionForm.RequiredDefinitionEditor
          className={styles.hideBorderBottom}
          checkboxLabel="Require this property for all repositories"
          checkboxCaption="Repositories that don't have an explicit value for this property will inherit the default value."
          defaultValueCaption="This is the default value that will be set for this property. Changing it has immediate effect, although on large organizations it may take minutes to be available in search results."
          setDefaultValueInputRef={setRef('defaultValue')}
          isRequired={getFieldValue('required')}
          valueType={valueType}
          defaultValue={getFieldValue('defaultValue') || ''}
          defaultValueError={getFieldError('defaultValue')}
          allowableDefaultValues={getFieldValue('allowedValues')}
          propertyName={getFieldValue('propertyName')}
          onRequiredChange={required => {
            updateField('required', required)
            if (!required) {
              updateField('requireExplicitValues', false)
            }
          }}
          onDefaultValueChange={value => updateField('defaultValue', value, {debounce: true})}
          requiredFormExtension={
            <FormControl className="ml-4 mt-2">
              <FormControl.Label>Require explicit user-specified values</FormControl.Label>
              <FormControl.Caption>
                When creating, transferring, or updating the property values of repositories, users must provide an
                explicit value.
              </FormControl.Caption>
              <Checkbox
                checked={getFieldValue('requireExplicitValues')}
                onChange={() => updateField('requireExplicitValues', !getFieldValue('requireExplicitValues'))}
              />
            </FormControl>
          }
        />
      </PropertyDefinitionForm.AdditionalSettingsSection>
    </PropertyDefinitionForm>
  )
}
